import * as React from 'react'
import { useState } from 'react'

import { Box, Button, Card, Form, Section, useToggle } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import LabeledInput from 'modules/core/components/LabeledInput'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { convertDate, dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { FrequencyOptions } from 'modules/surveys/lib/enums'
import { nearestPastHalfHour } from 'modules/surveys/lib/nearestPastHalfHour'
import type Survey from 'modules/surveys/store/models/v2/Survey'
import type { ISurvey } from 'modules/surveys/store/models/v2/Survey'

import ChangeEndDateSurveyModal from './CycleConfigurationModals/ChangeEndDateSurveyModal'
import { DEFAULT_FREQUENCY_OPTION, frequencySelectOptions } from './CycleOptions'
import HourPicker from './HourPicker'

interface Props {
  survey: Survey
}

const updateForm = (survey: ISurvey, form: FactorialForm) => {
  const startOnUtcDateTime = moment.utc(survey.start_on + ' ' + survey.scheduled_time).local()

  const start_on = dateToString(startOnUtcDateTime, 'ui')
  const scheduled_time = startOnUtcDateTime.format('HH:mm')
  const end_on = survey.end_on ? dateToString(moment(survey.end_on), 'ui') : null
  const { duration_in_days, frequency, source } = survey

  form.setValues({ start_on: start_on })
  form.setValues({ end_on: end_on })
  form.setValues({ scheduled_time: scheduled_time })
  form.setValues({ duration_in_days: duration_in_days })
  form.setValues({ frequency: frequency })
  form.setValues({ source: source })
}

export interface SurveyFactorialFormSchema {
  start_on: string
  scheduled_time: string
  duration_in_days: string
  frequency: FrequencyOptions
  end_on: string
  source: string
}

export const factorialFormFromSurvey = (survey: Survey): SurveyFactorialFormSchema => {
  const startOnUtcDateTime = survey.startOnUtcDateTime

  const surveyFormValues = {
    start_on: startOnUtcDateTime
      ? dateToString(startOnUtcDateTime.local(), 'ui')
      : dateToString(moment().startOf('day'), 'ui'),
    scheduled_time: startOnUtcDateTime
      ? survey.startOnUtcDateTime.local().format('HH:mm')
      : nearestPastHalfHour().format('HH:mm'),
    duration_in_days: survey.get('duration_in_days') ?? '',
    frequency: (survey.get('frequency') as FrequencyOptions) ?? DEFAULT_FREQUENCY_OPTION,
    end_on: survey.get('end_on') ? dateToString(moment(survey.get('end_on')), 'ui') : '',
    source: survey.get('source') ? survey.attributes.source : '',
  }

  return surveyFormValues
}

const prepareScheduleUTCRequestData = (form: FactorialForm) => {
  const startDayFromUI = convertDate(form.get('start_on').value, 'ui', 'backend')
  const startDateTimeLocal = moment(startDayFromUI + ' ' + form.get('scheduled_time').value).local()

  return {
    start_on: dateToString(moment.utc(startDateTimeLocal), 'backend'),
    scheduled_time: moment.utc(startDateTimeLocal).format('HH:mm'),
    duration_in_days: form.get('duration_in_days').value
      ? form.get('duration_in_days').value
      : null,
    frequency: form.get('frequency').value ? form.get('frequency').value : null,
    end_on: form.get('end_on').value
      ? convertDate(form.get('end_on').value, 'ui', 'backend')
      : null,
  }
}

function CycleConfiguration({ survey }: Props) {
  const initalFormValues = factorialFormFromSurvey(survey)

  const form = React.useRef(
    new FactorialForm(initalFormValues, {
      start_on: 'string',
      scheduled_time: 'string',
      duration_in_days: 'string',
      frequency: 'string',
      end_on: 'string',
      source: 'string',
    })
  ).current

  const [changeEndDateSurveyModalOpen, toggleChangeEndDateSurveyModal] = useToggle(false)

  const { running, performAction: submit } = useAsyncAction(async () => {
    const requestData = prepareScheduleUTCRequestData(form)
    const result = await survey.rpc('schedule', requestData)

    updateForm(result, form)
    form.cleanAll()
    survey.fetch()
  })

  const isCycle = survey.get('frequency') !== 'single'
  const [open, toggleOpen] = useState(isCycle)
  const toggleRepeatSurvey = (repeat: boolean) => {
    toggleOpen(repeat)
    form.setValues(repeat ? { frequency: 'monthly' } : { frequency: 'single', end_on: null })
    form.setValues({ duration_in_days: '' })
  }
  const today = moment().startOf('day')
  const tomorrow = today.add(1, 'days')
  const cycleOptions = frequencySelectOptions()
  const isSingleSurveySelected = form.get('frequency').value === 'single'
  const isSurveyActiveAndCycleHasAnswers = survey.isActive && survey.cycleHasAnswers
  const startDatetimeReadOnly = survey.isActive
  const cycleConfigurationReadOnly = survey.isFinished && survey.cycleHasAnswers
  const isClimateSurvey = form.get('source').value === 'climate_survey'

  const showConfirmationModalOrSubmit = () => {
    if (isSurveyActiveAndCycleHasAnswers && survey.isDaysToAnswerSet) {
      toggleChangeEndDateSurveyModal(true)()
    } else {
      submit()
    }
  }

  return (
    <Section
      illustration="calendar-clock"
      title={i18n.t('surveys.form.settings.cycle_configuration.title')}
      description={i18n.t('surveys.form.settings.cycle_configuration.description')}
    >
      <Form onSubmit={() => showConfirmationModalOrSubmit()} padding={null}>
        <Box gap="s32">
          <Box
            gap="s16"
            aria-label="Set start date configuration"
            background="grey200"
            borderRadius={{ all: 'abs008' }}
            width="full"
            padding="s32"
          >
            <Box gap="s8">
              <DayPicker
                label={i18n.t('surveys.form.settings.cycle_configuration.frequency.start_day')}
                minDate={moment().startOf('day')}
                value={form.get('start_on').value}
                onChange={(date) => form.setValues({ start_on: date })}
                readonly={startDatetimeReadOnly || cycleConfigurationReadOnly}
              />
              <HourPicker
                label={i18n.t('surveys.form.settings.cycle_configuration.frequency.start_time')}
                time={form.get('scheduled_time').value}
                allowCropping={survey.isDraft}
                date={form.get('start_on').value}
                readonly={startDatetimeReadOnly || cycleConfigurationReadOnly}
                onChange={(time) => form.setValues({ scheduled_time: time })}
              />
            </Box>
            <Box gap="s8">
              <TextField
                placeholder={i18n.t(
                  'surveys.form.settings.cycle_configuration.frequency.days_to_answer.placeholder'
                )}
                type="number"
                min={0}
                readonly={cycleConfigurationReadOnly}
                secondaryLabel={
                  <InformationTooltip
                    information={i18n.t(
                      'surveys.form.settings.cycle_configuration.frequency.days_to_answer.tooltip'
                    )}
                    icon="question"
                  />
                }
                value={form.get('duration_in_days').value}
                onChange={(value) =>
                  form.setValues({ duration_in_days: Number(value) ? Number(value) : '' })
                }
                label={i18n.t(
                  'surveys.form.settings.cycle_configuration.frequency.days_to_answer.label'
                )}
              />
            </Box>
          </Box>
          <Box gap="s16" aria-label="Set frequency configuration" width="full">
            <Card>
              <Card.Title size="small">
                {i18n.t('surveys.form.settings.cycle_configuration.frequency.title')}
              </Card.Title>
              <Card.Description>
                {i18n.t('surveys.form.settings.cycle_configuration.frequency.description')}
              </Card.Description>
              <Card.ExpandableContent
                expanded={open}
                onExpandChange={toggleRepeatSurvey}
                disabled={isClimateSurvey || cycleConfigurationReadOnly}
              >
                <Box gap="s16" paddingX="s16" paddingY="s8">
                  <Select
                    readonly={cycleConfigurationReadOnly}
                    label={i18n.t('surveys.form.start.form.labels.schedule')}
                    value={form.get('frequency').value}
                    options={cycleOptions}
                    onChange={(value) => form.setValues({ frequency: value })}
                  />
                  <LabeledInput
                    readonly={cycleConfigurationReadOnly}
                    isLegend
                    label={i18n.t('surveys.form.settings.cycle_configuration.repeat_until.label')}
                    secondaryLabel={
                      <InformationTooltip
                        information={i18n.t('surveys.form.start.form.labels.information')}
                        icon="question"
                      />
                    }
                  >
                    <DayPicker
                      readonly={cycleConfigurationReadOnly}
                      minDate={tomorrow}
                      value={form.get('end_on').value}
                      onChange={(date) => form.setValues({ end_on: date })}
                      placeholder={i18n.t(
                        'surveys.form.settings.cycle_configuration.repeat_until.placeholder'
                      )}
                      withNullLabel={i18n.t('surveys.form.start.form.labels.no_due_date')}
                      withNull
                    />
                  </LabeledInput>
                </Box>
              </Card.ExpandableContent>
            </Card>
          </Box>
          {form.isDirty && (
            <Box alignItems="flexEnd">
              <Button disabled={running} submit>
                {i18n.t('common.save')}
              </Button>
            </Box>
          )}
        </Box>
      </Form>
      {changeEndDateSurveyModalOpen && (
        <ChangeEndDateSurveyModal
          form={form}
          survey={survey}
          onClose={toggleChangeEndDateSurveyModal(false)}
          onSubmit={submit}
          running={running}
          isSingleSurveySelected={isSingleSurveySelected}
        />
      )}
    </Section>
  )
}

export default observer(CycleConfiguration)
